import './styles/fonts.css';
import './styles/app.css';

/*
 * Постоянная часть бандла — то, что нужно на каждой странице: навигация, инициализация
 * модалок, подстановка ленивых картинок, аналитика.
 *
 * Всё остальное подключается динамическим import() по наличию своего корневого
 * селектора. Причина: раньше app.js импортировал все модули статически, и вместе с
 * ними на каждую страницу ехали Vue + Pinia (квизы), fancybox (галерея, комментарии),
 * swiper (шесть модулей) и inputmask — суммарно 862 KB raw / 233 KB gzip на страницу,
 * из которых типовой странице нужна малая часть. Подробности и замеры —
 * docs/frontend-plan.md, P0-3.
 *
 * Селекторы ниже совпадают с теми, по которым модуль и сам себя включал, поэтому
 * поведение не меняется — меняется только момент загрузки кода. Там, где у модуля
 * несколько точек входа, селекторы перечислены через запятую: недогрузить модуль
 * хуже, чем изредка загрузить лишний.
 *
 * ВАЖНО: модуль, подключённый через import(), может выполниться уже после
 * DOMContentLoaded. Подписываться на это событие внутри таких модулей нельзя —
 * для них есть onDomReady из ./js/dom-ready.
 */
import './js/utils';
import './js/menu';
import './js/navbar';
import './js/micromodal';
import './js/images';
import './js/metrika';

import { QUIZ_SELECTORS } from './js/quiz-list';
import { loadCaptcha } from './js/components/utils/captcha';
import { warmUp } from './js/warm-up';
import { whenStylesReady } from './js/styles-ready';

// Модуль подключается, когда пришёл полный app.css. При блокирующем app.css это сразу; в
// режиме встроенных стилей (app.inline_styles) до его прихода у страницы только стили её
// собственной разметки, а блок, который рисует скрипт, остался бы без оформления.
const whenPresent = (selector, load) => {
  if (document.querySelector(selector)) {
    whenStylesReady(load);
  }
};

const afterLoad = (fn) => {
  if (document.readyState === 'complete') {
    fn();
  } else {
    window.addEventListener('load', fn, { once: true });
  }
};

// Vue + Pinia (~300 KB) — только страницы с квизом или формой заявки.
whenPresent(QUIZ_SELECTORS, () => import('./js/quiz'));
whenPresent('#call-page-form', () => import('./js/call-page-form'));

// fancybox
whenPresent('.gallery__swiper', () => import('./js/gallery'));
whenPresent('.lessonDetails, .lessonForm', () => {
  import('./js/comments/comments');
  import('./js/comments/file-upload');
});

// swiper
whenPresent('.team__swiper', () => import('./js/team'));
whenPresent('.certificates__swiper', () => import('./js/certificates'));
whenPresent('.colleagues', () => import('./js/colleagues'));
whenPresent('.review', () => import('./js/reviews/review').then((m) => m.init?.()));

// swiper + sticky-js
whenPresent('.catalogItem', () => import('./js/catalog'));

// inputmask
whenPresent('.masked_input', () => import('./js/masked-input-init'));

/*
 * video.js (~200 KB, самая длинная задача на старте). Все плееры на сайте ниже первого
 * экрана, поэтому чанк ждёт конца загрузки страницы и приближения плеера к экрану;
 * скрытый вариант плеера (display: none) с вьюпортом не пересекается и загрузку не запускает.
 *
 * До video.js ролик с постером — это просто картинка: постер задан CSS-фоном
 * (см. widgets/video/video.html.twig), а штатные элементы управления браузера сняты —
 * с ними пустой <video> закрашивается тёмной подложкой и фон не виден. Атрибут controls
 * остаётся в разметке для случая, когда скрипты не работают.
 *
 * Клик по такой картинке до прихода плеера запускает ролик сразу, штатным плеером браузера:
 * отложить запуск до загрузки video.js нельзя (телефон разрешает воспроизведение только
 * в момент нажатия), а создать плеер поверх играющего ролика — тоже: video.js при создании
 * перезагружает источник и сбрасывает воспроизведение. Поэтому такой ролик помечается
 * data-native-playback, и assets/js/video.js его не трогает.
 */
whenPresent('.video-js', () => {
  document.querySelectorAll('video.vid--poster').forEach((video) => {
    video.controls = false;
    video.addEventListener('click', () => {
      // плеер уже создан — нажатие обработает он сам
      if (video.classList.contains('vjs-tech')) {
        return;
      }
      video.dataset.nativePlayback = '';
      video.controls = true;
      const started = video.play();
      started && started.catch(() => {});
    }, { once: true });
  });

  afterLoad(() => {
    const nearViewport = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        nearViewport.disconnect();
        import('./js/video');
      }
    }, { rootMargin: '1200px 0px' });
    document.querySelectorAll('.video-js').forEach((el) => nearViewport.observe(el));
  });
});

// Яндекс.Карты грузятся внутри модуля по IntersectionObserver — см. P0-4.
whenPresent('[data-map-coords]', () => import('./js/catalog-map'));

// Остальное — без тяжёлых зависимостей, но и без нужды на большинстве страниц.
whenPresent('.catalogList', () => import('./js/catalog-list'));
whenPresent('.catalog__filters', () => import('./js/catalog-select'));
whenPresent('.achivement__date', () => import('./js/achivements'));
whenPresent('.questions__item', () => import('./js/questions'));
whenPresent('#reviewList', () => import('./js/otzyvy'));
whenPresent('.vacForm', () => import('./js/vacancy'));
whenPresent('#vacancyMobileSlider', () => import('./js/vacancy-header'));
whenPresent('.posts, .posts__list__show-more, .post__case_toggle', () => import('./js/articles'));
whenPresent('#program', () => import('./js/program'));
whenPresent('.lessonItem, .lessonDetails__videos', () => import('./js/lesson'));
whenPresent('.navbarDropdown', () => import('./js/navbar-dropdown'));
whenPresent('.share-buttons', () => import('./js/share'));

/*
 * Модалки заявки и записи на просмотр — Vue-приложения (~100 KB gzip вместе с Vue
 * и Pinia). Их DOM скрыт до открытия, поэтому на старте они не нужны: чанк греется
 * в первой паузе после начала взаимодействия со страницей (./js/warm-up), а гарантированно
 * подключается в момент открытия модалки (assets/js/micromodal.js).
 */
whenPresent('#modal-bid-form', () => warmUp(() => import('./js/modal-bid')));
whenPresent('#modal-visit-form', () => warmUp(() => import('./js/modal-visit')));

/*
 * Капча форм (./js/components/utils/captcha; сама проверка запускается на сабмите).
 * Грузим заранее, а не в момент отправки: оба провайдера судят о посетителе по поведению
 * на странице, а при низкой оценке reCAPTCHA заявка отклоняется, так что скрипту нужно
 * время понаблюдать. Обычно — тем же прогревом после паузы; если посетитель уже встал
 * в поле формы — сразу, чтобы к отправке всё было готово.
 */
warmUp(() => loadCaptcha());
const loadCaptchaOnFormFocus = (event) => {
  if (event.target instanceof Element && event.target.matches('input, textarea, select')) {
    document.removeEventListener('focusin', loadCaptchaOnFormFocus);
    loadCaptcha();
  }
};
document.addEventListener('focusin', loadCaptchaOnFormFocus);
